<script lang="ts">
  import { onMount } from 'svelte';
  import { session } from '../lib/session';
  import { logout } from '../lib/api';
  import { push } from 'svelte-spa-router';
  import { customTablesStore, builtInTablesStore, loadCustomTables } from '../lib/custom-tables-store';
  import Icon from './Icon.svelte';
  import { tableIconName } from '../lib/icons';

  /** Мета built-in таблиць (назва/іконка/колір з налаштувань) із fallback. */
  const usersMeta = $derived($builtInTablesStore['users']);
  const paymentsMeta = $derived($builtInTablesStore['payments']);

  let { active = 'overview', children }: { active?: string; children: import('svelte').Snippet } = $props();

  async function onLogout() {
    await logout();
    push('/login');
  }

  const initials = $derived($session ? $session.email.slice(0, 2).toUpperCase() : '?');
  const isAdmin = $derived($session?.role === 'ADMIN');

  onMount(() => {
    if ($session) loadCustomTables();
  });

  let sidebarOpen = $state(false);
  function closeSidebar() { sidebarOpen = false; }

  /** Згорнута панель (rail) — лише іконки. Лише desktop (lg+). Персиститься. */
  function readCollapsed(): boolean {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  }
  let collapsed = $state(readCollapsed());
  function toggleCollapsed() {
    collapsed = !collapsed;
    try { localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0'); } catch {}
  }
</script>

<!--
  Структура: flex-col, header + body.
  Body = flex-1 relative → sidebar absolute inset-y-0 на мобільному
  стартує рівно знизу header без hardcode висоти.
-->
<div class="h-screen flex flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
  <!-- Topbar — floating glass -->
  <header class="flex-none z-40 flex items-center gap-3 border-b border-[var(--color-border-2)] glass px-4 lg:px-6 py-2.5">
    <!-- Hamburger: mobile only -->
    <button
      class="lg:hidden grid place-items-center h-9 w-9 rounded-lg hover:bg-[var(--color-bg-3)] text-[var(--color-text-muted)] transition-colors"
      onclick={() => (sidebarOpen = !sidebarOpen)}
      aria-label="Відкрити меню"
    >
      <Icon name="menu" size={20} />
    </button>

    <!-- Згорнути/розгорнути панель: desktop only -->
    <button
      class="hidden lg:grid place-items-center h-9 w-9 rounded-lg hover:bg-[var(--color-bg-3)] text-[var(--color-text-muted)] transition-colors"
      onclick={toggleCollapsed}
      aria-label={collapsed ? 'Розгорнути панель' : 'Згорнути панель'}
      title={collapsed ? 'Розгорнути панель' : 'Згорнути панель'}
    >
      <Icon name="panelLeft" size={18} />
    </button>

    <div class="flex items-center gap-2.5 shrink-0">
      <div
        class="grid h-8 w-8 place-items-center rounded-xl text-xs font-bold text-white"
        style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2)); box-shadow: var(--shadow-pu);"
      >F</div>
      <div class="hidden sm:block leading-tight">
        <div class="text-[15px] font-semibold tracking-[-0.01em]">FocusOnYou</div>
        <div class="text-[9.5px] uppercase tracking-[1.4px] text-[var(--color-text-muted)] font-medium">
          Marketing Analytics
        </div>
      </div>
    </div>

    <div class="flex-1 hidden md:flex justify-center">
      <div class="group w-[22rem] flex items-center gap-2 rounded-xl border border-[var(--color-border-2)] bg-[var(--color-surface-2)] px-3 py-2 text-[13px] text-[var(--color-text-muted-2)] transition-shadow focus-within:ring-2 focus-within:ring-[var(--color-pu)]/30">
        <Icon name="search" size={15} class="text-[var(--color-text-muted)]" />
        <span class="truncate">Пошук користувача, тарифу, події…</span>
        <span class="ml-auto rounded-md border border-[var(--color-border-3)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] font-medium">⌘K</span>
      </div>
    </div>

    <div class="ml-auto flex items-center gap-2">
      <button class="grid h-9 w-9 place-items-center rounded-full border border-[var(--color-border-2)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:text-[var(--color-pu)] hover:bg-[var(--color-pu-light)] shadow-xs transition-colors" aria-label="Сповіщення">
        <Icon name="bell" size={16} />
      </button>
      <button
        onclick={onLogout}
        class="flex items-center gap-2 rounded-full border border-[var(--color-border-2)] py-1 pl-2 pr-1 sm:pl-3 shadow-xs hover:bg-[var(--color-bg-3)] transition-colors"
      >
        <div class="hidden sm:block text-right">
          <div class="text-xs font-semibold">{$session?.email ?? '—'}</div>
          <div class="text-[9px] uppercase tracking-[1px] text-[var(--color-text-muted)]">{$session?.role}</div>
        </div>
        <div
          class="grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold text-white"
          style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
        >{initials}</div>
      </button>
    </div>
  </header>

  <!-- Body: sidebar + main. flex-1 min-h-0 = заповнює viewport під header. -->
  <div class="flex flex-1 min-h-0 relative">
    <!-- Mobile backdrop: absolute в межах body-div (нижче header) -->
    {#if sidebarOpen}
      <div
        class="absolute inset-0 z-40 bg-black/30 lg:hidden"
        onclick={closeSidebar}
        role="presentation"
      ></div>
    {/if}

    <!-- Sidebar -->
    <aside
      class="absolute inset-y-0 left-0 z-50 w-56 shrink-0 overflow-y-auto overflow-x-hidden
             border-r border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-3
             transition-transform duration-200 lg:transition-[width]
             {sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
             lg:relative lg:translate-x-0 lg:z-auto {collapsed ? 'lg:w-[68px]' : 'lg:w-56'}"
      class:sb-rail={collapsed}
    >
      <a href="#/" onclick={closeSidebar} class="sb-item" class:active={active === 'overview'} title="Дашборд">
        <span class="sb-icon"><Icon name="dashboard" size={17} /></span><span class="sb-label">Дашборд</span>
      </a>

      <div class="sb-group">Таблиці</div>
      <a href="#/persons" onclick={closeSidebar} class="sb-item" class:active={active === 'persons'} title={usersMeta?.name ?? 'Користувачі'}>
        <span class="sb-icon" style={active === 'persons' || !usersMeta ? '' : `color:${usersMeta.color}`}><Icon name={usersMeta ? tableIconName(usersMeta.icon) : 'users'} size={17} /></span><span class="sb-label">{usersMeta?.name ?? 'Користувачі'}</span>
      </a>
      <a href="#/payments" onclick={closeSidebar} class="sb-item" class:active={active === 'payments'} title={paymentsMeta?.name ?? 'Платежі'}>
        <span class="sb-icon" style={active === 'payments' || !paymentsMeta ? '' : `color:${paymentsMeta.color}`}><Icon name={paymentsMeta ? tableIconName(paymentsMeta.icon) : 'payments'} size={17} /></span><span class="sb-label">{paymentsMeta?.name ?? 'Платежі'}</span>
      </a>
      {#each $customTablesStore as t (t.slug)}
        <a href={`#/tables/${t.slug}`} onclick={closeSidebar} class="sb-item" class:active={active === `table:${t.slug}`} title={t.name}>
          <span class="sb-icon" style={active === `table:${t.slug}` ? '' : `color:${t.color}`}><Icon name={tableIconName(t.icon)} size={17} /></span><span class="sb-label">{t.name}</span>
        </a>
      {/each}

      <div class="sb-group">Аналітика</div>
      <a href="#/cohorts" onclick={closeSidebar} class="sb-item" class:active={active === 'cohorts'} title="Когорти / Retention">
        <span class="sb-icon"><Icon name="cohorts" size={17} /></span><span class="sb-label">Когорти / Retention</span>
      </a>

      <div class="sb-group">Екосистема</div>
      <a href="#/funnels" onclick={closeSidebar} class="sb-item" class:active={active === 'funnels'} title="Воронки">
        <span class="sb-icon"><Icon name="funnels" size={17} /></span><span class="sb-label">Воронки</span>
      </a>
      {#if isAdmin}
        <a href="#/automations" onclick={closeSidebar} class="sb-item" class:active={active === 'automations'} title="Автоматизації">
          <span class="sb-icon"><Icon name="automations" size={17} /></span><span class="sb-label">Автоматизації</span>
        </a>
      {/if}
      {#if isAdmin}
        <a href="#/settings/products" onclick={closeSidebar} class="sb-item" class:active={active === 'settings-products'} title="Продукти">
          <span class="sb-icon"><Icon name="products" size={17} /></span><span class="sb-label">Продукти</span>
        </a>
      {/if}

      {#if isAdmin}
        <div class="mt-4 border-t border-[var(--color-border-3)] pt-3">
          <a
            href="#/settings"
            onclick={closeSidebar}
            class="sb-item"
            class:active={active === 'settings' || (active.startsWith('settings-') && active !== 'settings-products') || active === 'audit'}
            title="Налаштування"
          >
            <span class="sb-icon"><Icon name="settings" size={17} /></span><span class="sb-label">Налаштування</span>
          </a>
        </div>
      {/if}
    </aside>

    <!-- Main: flex-1, overflow-y-auto = скролиться всередині, не вся сторінка -->
    <main class="flex-1 min-w-0 overflow-y-auto amb-page p-4 sm:p-5 lg:p-7">
      {@render children()}
    </main>
  </div>
</div>

<style>
  :global(.sb-item) {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 11px;
    border-radius: 8px;
    font-size: 12.5px;
    color: var(--color-text);
    cursor: pointer;
    margin-bottom: 2px;
    text-decoration: none;
    font-weight: 500;
  }
  :global(.sb-item:hover) {
    background: var(--color-pu-light);
    color: var(--color-pu-3);
  }
  :global(.sb-item.active) {
    background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));
    color: white;
    box-shadow: 0 4px 10px rgba(123, 79, 190, 0.3);
  }
  :global(.sb-item.active .sb-icon) {
    color: white;
  }
  :global(.sb-icon) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    color: var(--color-text-muted);
  }
  :global(.sb-emoji) {
    font-size: 14px;
  }
  :global(.sb-group) {
    padding: 14px 12px 4px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--color-text-muted-2);
  }

  /* Rail (згорнута панель) — лише desktop. Mobile slide-over завжди з підписами. */
  @media (min-width: 1024px) {
    :global(.sb-rail .sb-label) { display: none; }
    :global(.sb-rail .sb-item) {
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }
    /* Групи-заголовки → тонкий розділювач */
    :global(.sb-rail .sb-group) {
      height: 1px;
      padding: 0;
      margin: 10px 8px;
      background: var(--color-border-3);
      overflow: hidden;
      text-indent: -9999px;
    }
  }
</style>
